import { ROLE_LABELS } from '@bo/constants/userRoles';
import UserForm from '@boComponents/UserForm';
import UserService from '@boServices/userService';
import { faPenToSquare, faPlus, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import Button from '@sharedComponents/Button';
import ConfirmModal from '@sharedComponents/ConfirmModal';
import FlexBox from '@sharedComponents/FlexBox';
import Panel from '@sharedComponents/Panel';
import Table from '@sharedComponents/Table';
import Tag from '@sharedComponents/Tag';
import { showToast } from '@sharedComponents/Toast';
import Typography from '@sharedComponents/Typography';
import { useModal } from '@sharedContexts/ModalContext';
import { useSidebar } from '@sharedContexts/SidebarContext';
import { formatDate } from '@utils/date';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import rowStyle from '@sharedComponents/Row/style.module.css';

const userService = new UserService();

const Users = () => {
    const { t } = useTranslation('bo');
    const [isLoading, setIsLoading] = useState(true);
    const [items, setItems] = useState([]);
    const { actions: sidebarActions } = useSidebar();
    const { actions: modalActions } = useModal();

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await userService.getUsers();
                setItems(response.items);
                setIsLoading(false);
            } catch (error) {
                showToast({
                    type: 'error',
                    text: t('An error occurred while fetching users. Please try again.'),
                });
                setIsLoading(false);
            }
        };
        fetchUsers();
    }, [t]);

    const submitUserForm = async (userData, userId) => {
        // if userId is provided, update the user, otherwise create a new user
        try {
            let response;
            if (userId) {
                response = await userService.updateUser(userId, userData);
                setItems((prevItems) => prevItems.map((item) => (item.uuid === userId ? response : item)));
            } else {
                response = await userService.createUser(userData);
                setItems((prevItems) => [...prevItems, response]);
            }
            sidebarActions.close();
        } catch (error) {
            showToast({
                type: 'error',
                text: t('An error occurred while submitting the form. Please try again.'),
            });
        }
    };

    const openCreate = () =>
        sidebarActions.open({
            title: t('Create a user '),
            component: () => <UserForm onSubmit={submitUserForm} />,
            onClose: () => sidebarActions.close(),
            isClosable: true,
        });

    const openEdit = (user) =>
        sidebarActions.open({
            title: t('Modify user'),
            component: () => <UserForm user={user} onSubmit={submitUserForm} />,
            onClose: () => sidebarActions.close(),
            isClosable: true,
        });

    const deleteUser = async (user) => {
        await userService.deleteUser(user.uuid);
        setItems((prevItems) => prevItems.filter((item) => item.uuid !== user.uuid));
    };

    const confirmDeleteUser = (user) =>
        modalActions.open({
            component: ConfirmModal,
            title: t('Delete user'),
            size: 's',
            message: t('Are you sure you want to delete {{email}}?', { email: user.email }),
            cancelLabel: t('Cancel'),
            confirmLabel: t('Confirm deletion'),
            confirmTheme: 'danger',
            onCancel: () => modalActions.close(),
            onConfirm: async () => {
                await deleteUser(user);
                modalActions.close();
            },
        });

    const columns = [
        { key: 'firstName', label: t('First Name'), tdClassName: rowStyle.td, render: (user) => user.firstName },
        { key: 'lastName', label: t('Last Name'), tdClassName: rowStyle.td, render: (user) => user.lastName },
        { key: 'email', label: t('Email'), tdClassName: rowStyle.td, render: (user) => user.email },
        {
            key: 'roles',
            label: t('Roles'),
            tdClassName: rowStyle.td,
            render: (user) => (
                <FlexBox gap="xxs" flexWrap="wrap">
                    {(user.roles ?? []).map((role) => (
                        <Tag key={role} color="secondary" textColor="branded-primary">
                            {t(ROLE_LABELS[role] ?? role)}
                        </Tag>
                    ))}
                </FlexBox>
            ),
        },
        {
            key: 'lastLoginAt',
            label: t('Last login'),
            tdClassName: rowStyle.td,
            render: (user) => (user.lastLoginAt ? formatDate(user.lastLoginAt) : '—'),
        },
        {
            key: 'actions',
            ariaLabel: t('Actions'),
            tdClassName: rowStyle.tdActions,
            pinned: true,
            render: (user) => (
                <FlexBox gap="xs">
                    <Button ghost icon={faPenToSquare} action={() => openEdit(user)} title={t('Edit')} />
                    <Button ghost theme="danger" icon={faTrashCan} action={() => confirmDeleteUser(user)} title={t('Delete')} />
                </FlexBox>
            ),
        },
    ];

    return (
        <FlexBox flexDirection="column" gap="l" additionnalStyles={{ padding: 'var(--space-l) var(--space-xl)' }}>
            <FlexBox justifyContent="space-between" alignItems="center">
                <Typography fontSize="xl" fontWeight="bold" color="branded-primary" seo={1}>
                    {t('Users')}
                </Typography>
                <Button icon={faPlus} label={t('Add User')} action={openCreate} />
            </FlexBox>

            <Panel>
                <Table columns={columns} items={items} loading={isLoading} borderless getRowKey={(user) => user.uuid} />
            </Panel>
        </FlexBox>
    );
};

export default Users;
